<script setup lang="ts">
import dayjs from 'dayjs'
import { ref } from 'vue'
import { useVueInject } from '@/hooks/vue'
import { pluginKeys } from '@/plugins/dayjs'
import VueDatepicker from '@vuepic/vue-datepicker'
import { useVModel } from '@vueuse/core'
import { findColorValue } from '@/utils/style'
import { IDatepickerProps } from '@/types/common/datepicker'
import { useActions } from '.'

const injectedDayjs = useVueInject<typeof dayjs>(pluginKeys.DAYJS)

interface IProps {
  modelValue: IDatepickerProps['modelValue']
  holidays: IDatepickerProps['holidays']
  minDate: IDatepickerProps['minDate']
  maxDate: IDatepickerProps['maxDate']
  startDate: IDatepickerProps['startDate']
  inputStyles: IDatepickerProps['inputStyles']
  placeholder?: IDatepickerProps['placeholder']
  iconColor?: IDatepickerProps['iconColor']
  displayFormat?: IDatepickerProps['displayFormat']
  closed?: IDatepickerProps['closed']
}

const props = withDefaults(defineProps<IProps>(), {
  placeholder: '日付を選択',
  iconColor: 'dark-brown',
  displayFormat: 'yyyy-MM-dd',
  closed() {
    return
  },
})

const emit = defineEmits(['update:modelValue'])
const modelValue = useVModel(props, 'modelValue', emit)

const actions = useActions(emit, props, injectedDayjs)
const teleportTargetElement = ref<HTMLInputElement>()
</script>

<template>
  <div class="vue-datepicker">
    <VueDatepicker
      v-model="modelValue"
      model-type="yyyy-MM-dd"
      :format="props.displayFormat"
      :enable-time-picker="false"
      :clearable="false"
      :highlight="(props.holidays ?? []).map((item) => item.date)"
      :min-date="props.minDate"
      :max-date="props.maxDate"
      :start-date="props.startDate"
      :prevent-min-max-navigation="!!props.minDate || !!props.maxDate"
      :week-start="0"
      timezone="Asia/Tokyo"
      locale="ja"
      position="left"
      :month-change-on-arrows="false"
      :offset="0"
      :teleport="teleportTargetElement"
      :auto-position="false"
      :month-change-on-scroll="false"
      :no-swipe="true"
      auto-apply
      no-today
      keep-action-row
      hide-input-icon
      @closed="props.closed"
    >
      <template #dp-input="{ value, onInput, onEnter, onTab }">
        <input
          :style="props.inputStyles"
          class="custom-input"
          inputmode="none"
          placeholder="日付を選択"
          :value="value"
          autocomplete="off"
          @input="onInput"
          @keydown.enter="onEnter"
          @keydown.tab="onTab"
        />
        <i class="input-icon"></i>
      </template>
      <template #month-year="{ month, year, handleMonthYearChange }">
        <div class="custom-month-year-component">
          <div
            class="custom-month-year-component-prev"
            role="button"
            :aria-disabled="actions.isDisabledMonthYearButton(false, [year, month])"
            @click="handleMonthYearChange(false)"
          ></div>
          <div class="custom-month-year-component-current">{{ year }}年 {{ month + 1 }}月</div>
          <div
            class="custom-month-year-component-next"
            role="button"
            :aria-disabled="actions.isDisabledMonthYearButton(true, [year, month])"
            @click="handleMonthYearChange(true)"
          ></div>
        </div>
      </template>
      <template #action-row="{ closePicker }">
        <div class="custom-action-row-component">
          <button class="custom-action-row-component-clear" @click="actions.handleClear(closePicker)">クリア</button>
          <button class="custom-action-row-component-close" @click="closePicker()">閉じる</button>
        </div>
      </template>
    </VueDatepicker>
    <div ref="teleportTargetElement"></div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/mixins/form' as form-mixins;
.vue-datepicker {
  .custom-input {
    @include form-mixins.form-input;
    cursor: pointer;
  }
  .input-icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    content: '';
    width: 16px;
    height: 16px;
    background-color: v-bind('findColorValue(iconColor)');
    -webkit-mask: url(/assets/images/iconmonstr-calendar-5-icon.svg) center / 16px no-repeat;
    mask: url(/assets/images/iconmonstr-calendar-5-icon.svg) center / 16px no-repeat;
    cursor: pointer;
  }
  .custom-month-year-component {
    display: flex;
    gap: 2px;
    color: v-bind('findColorValue("white")');
    font-size: 15px;
    text-align: center;
    width: 100%;
    .custom-month-year-component-current {
      flex-grow: 1;
      background: v-bind('findColorValue("light-blue")');
      padding: 4px 0;
      white-space: nowrap;
      overflow: hidden;
    }
    .custom-month-year-component-prev,
    .custom-month-year-component-next {
      flex-basis: 32px;
      background-color: v-bind('findColorValue("light-blue")');
      background-position: center;
      background-size: 16px;
      background-repeat: no-repeat;
      &[aria-disabled='true'] {
        background-image: none;
      }
    }
    .custom-month-year-component-prev {
      background-image: url(/assets/images/arrow-prev-icon.svg);
    }
    .custom-month-year-component-next {
      background-image: url(/assets/images/arrow-next-icon.svg);
    }
  }
  .custom-action-row-component {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    padding: 8px;
    width: 100%;
    border-top: 1px solid v-bind('findColorValue("light-gray")');
    box-sizing: border-box;
    .custom-action-row-component-clear,
    .custom-action-row-component-close {
      background: v-bind('findColorValue("white")');
      border: 1px solid v-bind('findColorValue("light-gray")');
      color: v-bind('findColorValue("dark-brown")');
      font-size: 15px;
      padding: 4px 9px;
      cursor: pointer;
    }
  }
}
</style>
<style lang="scss">
$bg-color-sun: #fff1ed;
$bg-color-sat: #e9f5f8;
$bg-color-holiday: $bg-color-sun;

.vue-datepicker {
  height: 100%;
  .dp__input_wrap {
    width: 100%;
    height: 100%;
    position: relative;
  }
  .dp__menu {
    position: absolute;
    background: v-bind('findColorValue("color-f0f0f0")');
    width: 268px;
    font-size: 14px;
    user-select: none;
    padding: 3px;
    box-sizing: border-box;
    &:focus {
      outline: none;
    }
    .dp__arrow_top,
    .dp__arrow_bottom {
      display: none;
    }
  }
  .dp__menu_index {
    z-index: 99999;
  }
  .dp__calendar_wrap {
    background: v-bind('findColorValue("light-gray")');
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    .dp__calendar_header {
      display: flex;
      gap: 1px;
      width: 100%;
      padding: 1px 1px 0;
      box-sizing: border-box;
      color: v-bind('findColorValue("dark-brown")');
      font-weight: normal;
      white-space: nowrap;
      .dp__calendar_header_item {
        flex: 1 1 0;
        text-align: center;
        overflow: hidden;
        background: v-bind('findColorValue("light-cream")');
        padding: 2px 0;
        &:nth-of-type(7n) {
          color: v-bind('findColorValue("light-blue")');
        }
        &:nth-of-type(7n + 1) {
          color: v-bind('findColorValue("red")');
        }
      }
    }
    .dp__calendar_header_separator {
      display: none;
    }
    .dp__calendar {
      display: flex;
      flex-direction: column;
      gap: 1px;
      width: 100%;
      padding: 1px;
      box-sizing: border-box;
      .dp__calendar_row {
        display: flex;
        gap: 1px;
        white-space: nowrap;
        .dp__calendar_item {
          flex: 1 1 0;
          text-align: center;
          overflow: hidden;
          color: v-bind('findColorValue("dark-brown")');
          .dp__cell_inner {
            display: flex;
            align-items: center;
            text-align: center;
            justify-content: center;
            background: v-bind('findColorValue("white")');
            height: 34px;
            color: v-bind('findColorValue("dark-brown")');
            font-weight: bold;
            box-sizing: border-box;
            &.dp__active_date,
            &.dp__date_hover {
              &:hover {
                border: 2px solid v-bind('findColorValue("green")');
              }
            }
            &.dp__cell_highlight {
              background: $bg-color-holiday;
            }
            &.dp__cell_offset,
            &.dp__cell_disabled {
              background: v-bind('findColorValue("color-f0f0f0")');
              color: v-bind('findColorValue("light-gray")');
              font-weight: normal;
            }
          }
          &:nth-of-type(7n) {
            .dp__cell_inner:not(.dp__cell_offset):not(.dp__cell_disabled):not(.dp__cell_highlight) {
              background: $bg-color-sat;
            }
          }
          &:nth-of-type(7n + 1) {
            .dp__cell_inner:not(.dp__cell_offset):not(.dp__cell_disabled):not(.dp__cell_highlight) {
              background: $bg-color-sun;
            }
          }
        }
      }
    }
  }
  .dp__action_row {
    width: 100%;
    margin-top: 10px;
  }
  .dp__main {
    display: flex;
    height: 100%;
    user-select: none;
    box-sizing: border-box;
    > * {
      flex-grow: 1;
    }
  }
  .dp__pointer {
    cursor: pointer;
  }
}
</style>
